<template>
  <div class="news-list-page">
    <h1 class="page-title">ຂ່າວສານທັງໝົດ</h1>

    <!-- Category Filter -->
    <div class="filter-section">
      <button
        @click="handleCategoryChange(null)"
        :class="['filter-btn', { active: selectedCategory === null }]"
      >
        ທັງໝົດ
      </button>
      <button
        v-for="cat in categories"
        :key="cat.id"
        @click="handleCategoryChange(cat.id)"
        :class="['filter-btn', { active: selectedCategory === cat.id }]"
      >
        {{ cat.name }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-grid">
      <div v-for="n in 6" :key="n" class="skeleton-card"></div>
    </div>

    <!-- News Grid -->
    <div
      v-else-if="paginatedNews && paginatedNews.length > 0"
      class="news-grid"
    >
      <article
        v-for="newsItem in paginatedNews"
        :key="newsItem.id"
        class="news-card"
        @click="goToNews(newsItem.slug)"
      >
        <div class="card-image">
          <img
            :src="
              newsItem.featured_image ||
              'https://via.placeholder.com/400x250?text=No+Image'
            "
            :alt="newsItem.title"
            @error="handleImageError"
          />
          <div v-if="newsItem.category" class="category-overlay">
            {{ newsItem.category.name }}
          </div>
        </div>

        <div class="card-content">
          <h2 class="card-title">{{ newsItem.title }}</h2>

          <div class="card-footer">
            <time class="card-date">{{
              formatDate(newsItem.published_at)
            }}</time>
          </div>
        </div>
      </article>
    </div>

    <!-- Load More / Show Less Button -->
    <div v-if="!loading && filteredNews.length > 0" class="load-more-section">
      <button v-if="showLoadMore" @click="loadMore" class="load-more-btn">
        <svg
          class="btn-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
        ເພີ່ມເຕີມ
      </button>
      <button
        v-else-if="displayCount > itemsPerPage"
        @click="showLess"
        class="show-less-btn"
      >
        <svg
          class="btn-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
        ສະແດງໜ້ອຍລົງ
      </button>
    </div>

    <!-- No Data -->
    <div v-else-if="!loading" class="no-data">
      <svg
        class="no-data-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
      <p>ຂໍອະໄພ ພົບປັນຫາໃນການໂຫຼດຂ່າວສານໃນຂະນະນີ້</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRouter } from "vue-router";
import gql from "graphql-tag";

// Router
const router = useRouter();

// State
const selectedCategory = ref(null);
const itemsPerPage = 6;
const displayCount = ref(itemsPerPage);

// GraphQL Queries
const GET_CATEGORIES = gql`
  query GetCategories {
    categories(order_by: { order_number: asc }) {
      id
      name
      slug
    }
  }
`;

const GET_NEWS_LIST = gql`
  query GetNewsList {
    news(
      where: { status: { _eq: "published" } }
      order_by: { published_at: desc }
    ) {
      id
      title
      slug
      featured_image
      published_at
      category {
        id
        name
      }
    }
  }
`;

// Execute Queries
const { result: categoriesResult } = useQuery(GET_CATEGORIES);
const { result: newsResult, loading } = useQuery(GET_NEWS_LIST);

// Computed properties
const categories = computed(() => categoriesResult.value?.categories || []);
const newsList = computed(() => newsResult.value?.news || []);

const filteredNews = computed(() => {
  if (!selectedCategory.value) {
    return newsList.value;
  }
  return newsList.value.filter(
    (news) => news.category?.id === selectedCategory.value
  );
});

const paginatedNews = computed(() => {
  return filteredNews.value.slice(0, displayCount.value);
});

const showLoadMore = computed(() => {
  return displayCount.value < filteredNews.value.length;
});

// Methods
const loadMore = () => {
  displayCount.value += itemsPerPage;
};

const showLess = () => {
  displayCount.value = itemsPerPage;
  // Scroll to top smoothly
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const handleCategoryChange = (categoryId) => {
  selectedCategory.value = categoryId;
  displayCount.value = itemsPerPage; // Reset to initial display count
};

const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return dateString;

  const monthsLao = [
    "ມັງກອນ",
    "ກຸມພາ",
    "ມີນາ",
    "ເມສາ",
    "ພຶດສະພາ",
    "ມິຖຸນາ",
    "ກໍລະກົດ",
    "ສິງຫາ",
    "ກັນຍາ",
    "ຕຸລາ",
    "ພະຈິກ",
    "ທັນວາ",
  ];

  const day = date.getDate();
  const month = monthsLao[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

const goToNews = (slug) => {
  router.push({ name: "NewsDetail", params: { slug } });
};

const handleImageError = (event) => {
  event.target.src =
    "https://t3.ftcdn.net/jpg/02/72/45/58/360_F_272455816_mrPQJvA0gKafenXR2o78wR7a7bRytodW.jpg";
};
</script>

<style scoped>
.news-list-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.page-title {
  font-size: 2.5rem;
  color: #344873;
  margin-bottom: 30px;
  text-align: center;
  font-weight: 700;
}

/* ===== Filter Section ===== */
.filter-section {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 10px 24px;
  background: white;
  color: #4a5568;
  border: 2px solid #e2e8f0;
  border-radius: 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.filter-btn:hover {
  border-color: #b7191e;
  color: #b7191e;
}

.filter-btn.active {
  background: linear-gradient(135deg, #ea2127 0%, #b7191e 100%);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(198, 198, 198, 0.4);
}

/* ===== News Grid ===== */
.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.news-card {
  background: white;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  cursor: pointer;
}

.news-card:hover {
  transform: translateY(-0.75rem);
  box-shadow: 0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1);
}

.card-image {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: #f0f0f0;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.news-card:hover .card-image img {
  transform: scale(1.05);
}

.category-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 14px;
  background: rgba(234, 33, 39, 0.708);
  color: white;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.card-content {
  padding: 20px;
}

.card-title {
  font-size: 1.3rem;
  color: #344873;
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 600;
}

.card-date {
  display: block;
  color: #718096;
  font-size: 0.9rem;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.views {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4a5568;
  font-size: 0.9rem;
}

.views .icon {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

/* ===== Load More Section ===== */
.load-more-section {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 40px;
}

.load-more-btn,
.show-less-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.load-more-btn {
  background: linear-gradient(135deg, #ea2127 0%, #b7191e 100%);
  color: white;
}

.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(234, 33, 39, 0.4);
}

.show-less-btn {
  background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
  color: white;
}

.show-less-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 85, 104, 0.4);
}

.btn-icon {
  width: 20px;
  height: 20px;
  stroke-width: 2.5;
}

/* ===== Loading Skeleton ===== */
.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
}

.skeleton-card {
  height: 360px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ===== No Data ===== */
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 20px;
  color: #718096;
}

.no-data-icon {
  width: 80px;
  height: 80px;
  color: #cbd5e0;
  margin-bottom: 20px;
  stroke-width: 1.5;
}

.no-data p {
  font-size: 1.2rem;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  .news-grid,
  .loading-grid {
    grid-template-columns: 1fr;
  }
  .filter-section {
    gap: 8px;
  }
  .filter-btn {
    padding: 8px 16px;
    font-size: 0.85rem;
  }
  .load-more-btn,
  .show-less-btn {
    padding: 12px 24px;
    font-size: 0.9rem;
  }
}
</style>